import type { HTMLAttributes, ReactNode } from 'react';

export type ChipVariant = 'warning' | 'info' | 'neutral' | 'success' | 'blocked';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  variant: ChipVariant;
  children: ReactNode;
}

/**
 * Chip — outline pill with a small dot prefix and mono uppercase text. Sibling
 * to `<Pill>` (status colours via `currentColor`), but the Chip uses a soft
 * fill + tinted border + tinted text from a semantic state token trio:
 *   warning → `--st-warn-soft` / `--st-warn-line` / `--st-warn`
 *   info    → `--st-info-soft` / `--st-info-line` / `--st-info`
 *   neutral → `--st-neutral-soft` / `--st-neutral-line` / `--st-neutral`
 *   success → `--st-done-soft` / `--st-done-line` / `--st-done`
 *   blocked → `--st-error-soft` / `--st-blocked-line` / `--st-blocked`
 * Token-only chrome lives in `primitives.css` under `pn-chip`. Consumed by the
 * cross-item approval card (`<Chip variant="warning">Cross-item</Chip>`) and the
 * skills governance registry (`success` = approved, `blocked` = blocked-by-policy).
 */
export function Chip({ variant, children, className, ...rest }: ChipProps) {
  const cls = ['pn-chip', `pn-chip-${variant}`, className]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={cls} {...rest}>
      <span className="pn-chip-dot" aria-hidden="true" />
      {children}
    </span>
  );
}
